import { API_URL } from 'core/infra/environment'
import { HttpClient } from 'core/infra/http/HttpClient'
import { ShipperGuideClient } from 'core/infra/http/ShipperGuideClient'
import { createDate, compareAsc, isDate } from 'core/utils/date'

import { getOfferList } from 'legacy/services/offers'

import { TenderRepository } from 'tender/infra/TenderRepository'

import {
  type PendingResponse,
  createContractPendingResponse,
  createS2CPendingResponse,
  createTenderPendingResponse,
} from '../domain/PendingResponse'

function getDate(pendingResponse: PendingResponse): Date | undefined {
  if (pendingResponse.type === 'tender') {
    return pendingResponse.value.deadline
  } else {
    return createDate(pendingResponse.value.expires_at)
  }
}

export type FactoryDependencies = {
  /**
   * We are using a legacy service here instead of a Repository because in
   * this release we are using the legacy OfferCard to display the result and
   * it expects a RawLoadOffer.
   * In a future task (CM-584) we will change the OfferCard, then we can
   * refactor this use case to use a repository.
   */
  offerListGetter: typeof getOfferList
  tenderRepository: TenderRepository
}

export function getListOfPendingResponsesFactory({
  offerListGetter,
  tenderRepository,
}: FactoryDependencies) {
  return async function getListOfPendingResponsesImpl(config?: {
    signal?: AbortSignal
  }): Promise<Array<PendingResponse>> {
    const contractedOffersRequest = offerListGetter({
      params: { market_tags: 'contract' },
      signal: config?.signal,
    })
    const shootToCarrierOffersRequest = offerListGetter({
      params: { market_tags: 'manual' },
      signal: config?.signal,
    })
    const pendingTendersRequest = tenderRepository.getPendingTenders(config)

    const [contractedOffersResult, shootToCArrierOffersResult, pendingTendersResult] =
      await Promise.allSettled([
        contractedOffersRequest,
        shootToCarrierOffersRequest,
        pendingTendersRequest,
      ])

    let contractedOffers: Array<PendingResponse> = []
    let shootToCarrierOffers: Array<PendingResponse> = []
    let pendingTenders: Array<PendingResponse> = []

    if (contractedOffersResult.status === 'fulfilled') {
      contractedOffers = (contractedOffersResult.value?.data?.results ?? []).map(
        createContractPendingResponse
      )
    }

    if (shootToCArrierOffersResult.status === 'fulfilled') {
      shootToCarrierOffers = (shootToCArrierOffersResult.value?.data?.results ?? []).map(
        createS2CPendingResponse
      )
    }

    if (pendingTendersResult.status === 'fulfilled') {
      pendingTenders = pendingTendersResult.value.map(createTenderPendingResponse)
    }

    let result: Array<PendingResponse> = [
      ...contractedOffers,
      ...shootToCarrierOffers,
      ...pendingTenders,
    ]

    result.sort((left, right) => {
      const dateLeft = getDate(left)
      const dateRight = getDate(right)

      if (isDate(dateLeft) && isDate(dateRight)) {
        return compareAsc(dateLeft, dateRight)
      } else if (!isDate(dateLeft) && isDate(dateRight)) {
        return 1
      } else if (isDate(dateLeft) && !isDate(dateRight)) {
        return -1
      } else {
        return 0
      }
    })

    return result
  }
}

export function getListOfPendingResponses(config?: {
  signal?: AbortSignal
}): Promise<Array<PendingResponse>> {
  const httpClient = new HttpClient(API_URL)
  const shipperGuideHttpClient = new ShipperGuideClient()
  const tenderRepository = new TenderRepository({ httpClient, shipperGuideHttpClient })
  const newGetListOfPendingResponses = getListOfPendingResponsesFactory({
    offerListGetter: getOfferList,
    tenderRepository,
  })
  return newGetListOfPendingResponses(config)
}
